import client from '@/services/client'
import type {
  IModel3DAssignment,
  IModelType,
  UpdateModelPayload,
} from '@/types/EditorTypes'
import type {
  IFile,
  ContextValues,
  IListFile,
  IFileListItem,
  IFileUpdateForm,
} from '@/types/FileTypes'
import type {
  FlowPropertyStep,
  IFlow,
  IFlowBase,
  IFlowSignature,
  INode,
  INodeForm,
  INodeOption,
  INodeOptionForm,
  INodeOptionIndex,
  IOption,
  IOptionPropertyPayload,
  IOptionPropertyResponse,
  IStage,
  IStepsFlow,
} from '@/types/flow/FlowNodeTypes'
import type {
  ICreateFlowDraft,
  IFlowCreate,
  IFlowResult,
  ICopyFlowDraft,
  IGetStageResult,
  ICreateStage,
  IEditStage,
  IUpdateStageIndexBody,
  IUpdateStepStageIndexBody,
  ICreateFlowAssignmentPayload,
  IAssignmentUpdatePayload,
  ICreateModelPayload,
  IUpdateStepStageResponse,
  IModel3DAssignmentProperty,
  IFlowUpdatePayload,
  IOperationPropertiesResponse,
  IFlowsFilters,
  IFlowAssignmentsPayload,
  IFlowRulePropertiesResponse,
  ICreateModelGroupResonse,
  IModelGroupsResponse,
  ICreateModelGroupPayload,
  IUpdateModelGroup,
} from '@/types/flow/FlowPayloadTypes'
import type { IMatrixResponse } from '@/types/FlowMatrixTypes'

import { FlowCriteria } from './flowCriteria'
import { FlowCriterionValues } from './flowCriterionValues'
import { FlowLabels } from './flowLabels'

import type {
  AxiosProgressEvent,
  AxiosResponse,
  GenericAbortSignal,
} from 'axios'

const createFlowDraft = async (
  payload: ICreateFlowDraft
): Promise<AxiosResponse<IFlowResult>> => {
  return await client.post<IFlowResult>(`flows`, payload)
}

const cloneFlowDraft = async (
  flowUuid: string,
  payload: ICopyFlowDraft
): Promise<AxiosResponse<IFlow>> => {
  return await client.post(`flows/${flowUuid}/copy`, payload)
}

const deleteFlowDraft = async (flowUuid: string): Promise<AxiosResponse> => {
  return await client.delete(`flows/${flowUuid}`)
}

const getFlowsMatrix = async (): Promise<AxiosResponse<IMatrixResponse[]>> => {
  return await client.get<IMatrixResponse[]>(`flows/configuration`)
}

// Filters narrow the list to flows of any status using that criterion or value
const getFlows = async (
  filters: IFlowsFilters = {}
): Promise<AxiosResponse<IFlow[]>> => {
  return await client.get<IFlow[]>(`flows`, { params: filters })
}

const getFlowById = async (id: string): Promise<AxiosResponse<IFlow>> => {
  return await client.get<IFlow>(`flows/${id}`)
}

const updateFlow = async (
  flowUuid: string,
  payload: IFlowUpdatePayload
): Promise<AxiosResponse<IFlow>> => {
  return await client.patch(`flows/${flowUuid}`, payload)
}

const updateFlowDimensions = async (
  flowUuid: string,
  sceneLength: number
): Promise<AxiosResponse<IFlow>> => {
  return await client.patch(`flows/${flowUuid}/dimensions`, {
    sceneLength,
  })
}

const getNodeById = async (nodeId: string): Promise<AxiosResponse<INode>> => {
  return await client.get<INode>(`flows/steps/${nodeId}`)
}

const createNode = async (
  flowId: string,
  payload: IFlowCreate
): Promise<AxiosResponse<INode>> => {
  return await client.post<INode>(`flows/steps`, {
    ...payload,
    flowUuid: flowId,
  })
}

const getStages = async (
  flowUuid: string
): Promise<AxiosResponse<IGetStageResult[]>> => {
  const params = new URLSearchParams()
  params.append('flowUuid', flowUuid)
  return await client.get<IGetStageResult[]>(`flows/stages`, {
    params: params,
  })
}

const createStage = async (
  payload: ICreateStage
): Promise<AxiosResponse<IStage>> => {
  return await client.post<IStage>(`flows/stages`, payload)
}

const updateStage = async (
  stageUuid: string,
  payload: IEditStage
): Promise<AxiosResponse<IStage>> => {
  return await client.patch(`flows/stages/${stageUuid}`, payload)
}

const deleteStage = async (
  stageUuid: string
): Promise<AxiosResponse<string>> => {
  return await client.delete(`flows/stages/${stageUuid}`)
}

const updateStageIndex = async (
  flowUuid: string,
  payload: IUpdateStageIndexBody
): Promise<AxiosResponse<unknown>> => {
  return await client.put(`flows/${flowUuid}/stage-indexes`, payload)
}

const updateStepStage = async (
  stepUuid: string,
  stageUuid: string
): Promise<AxiosResponse<void>> => {
  return await client.patch<void>(`/flows/steps/${stepUuid}/stage`, {
    stageUuid: stageUuid,
  })
}

const updateStepStageIndex = async (
  stageUuid: string,
  payload: IUpdateStepStageIndexBody
): Promise<AxiosResponse<IUpdateStepStageResponse>> => {
  return await client.put(`flows/stages/${stageUuid}/step-indexes`, payload)
}

const updateNode = async (
  nodeId: string,
  payload: INodeForm
): Promise<AxiosResponse<INode>> => {
  return await client.patch<INode>(`flows/steps/${nodeId}`, payload)
}

const createNodeOption = async (
  nodeId: string,
  payload: INodeOptionForm
): Promise<AxiosResponse<INodeOption>> => {
  return await client.post<INodeOption>(`flows/options`, {
    ...payload,
    stepUuid: nodeId,
  })
}

const removeNodeOption = async (
  optionUuid: string
): Promise<AxiosResponse<void>> => {
  return await client.delete(`flows/options/${optionUuid}`)
}

const getAllOptionProperties = async (
  stepId: string
): Promise<AxiosResponse<IOptionPropertyResponse[]>> => {
  return await client.get<IOptionPropertyResponse[]>(
    `flows/option-properties/${stepId}`
  )
}

const updateOptionProperty = async (
  optionPropertyUuid: string,
  payload: IOptionPropertyPayload
): Promise<AxiosResponse<IOptionPropertyResponse[]>> => {
  return await client.patch(
    `flows/option-properties/${optionPropertyUuid}`,
    payload
  )
}

const getIntegrationProperties = async (): Promise<
  AxiosResponse<IOperationPropertiesResponse[]>
> => {
  return await client.get<IOperationPropertiesResponse[]>(
    `flows/options/integration-properties`
  )
}

const updateStepProperty = async <K extends FlowPropertyStep>(
  stepPropertyuuid: string,
  propertyStep: K,
  value: K extends 'infoValue' ? string : boolean
): Promise<AxiosResponse<void>> => {
  return await client.patch<void>(`flows/step-properties/${stepPropertyuuid}`, {
    [propertyStep]: value,
  })
}

const updateNodeOptionIndexes = async (
  nodeId: string,
  payload: INodeOptionIndex[]
): Promise<AxiosResponse<string>> => {
  return await client.put<string>(`flows/steps/${nodeId}/option-indexes`, {
    indexes: payload,
  })
}

const getNodeOption = async (
  optionUuid: string
): Promise<AxiosResponse<IOption>> => {
  return await client.get<IOption>(`flows/options/${optionUuid}`)
}

const getNodeStepsIndexes = async (
  flowUuid: string
): Promise<AxiosResponse<IStepsFlow[]>> => {
  const params = new URLSearchParams()
  params.append('flowUuid', flowUuid)

  return await client.get<IStepsFlow[]>(`flows/options/`, {
    params: params,
  })
}

const updateNodeOption = async (
  optionId: string,
  payload: INodeOptionForm
): Promise<AxiosResponse<INodeOption>> => {
  return await client.patch<INodeOption>(`flows/options/${optionId}`, payload)
}

const updateNodeJson = async (
  nodeId: string,
  payload: string
): Promise<AxiosResponse> => {
  return await client.patch(`flows/steps/${nodeId}/json`, {
    json: payload,
  })
}

const deleteNode = async (nodeId: string): Promise<AxiosResponse> => {
  return await client.delete(`/flows/steps/${nodeId}`)
}

const getFiles = async (): Promise<AxiosResponse<IFileListItem[]>> => {
  return await client.get<IFileListItem[]>(`/files`)
}

const updateFiles = async (
  form: IFileUpdateForm
): Promise<AxiosResponse<IFileListItem>> => {
  return await client.patch<IFileListItem>(`/files/${form.uuid}`, form)
}

const deleteFile = async (fileUuid: string): Promise<AxiosResponse> => {
  return await client.delete<IFileListItem[]>(`/files/${fileUuid}`)
}

const getFlowModels = async (): Promise<AxiosResponse<IListFile[]>> => {
  return await client.get<IListFile[]>(`/flows/models`)
}

const getFlowAssignments = async (
  payload: IFlowAssignmentsPayload
): Promise<AxiosResponse<IModel3DAssignment[]>> => {
  return await client.get<IModel3DAssignment[]>(`/flows/model-assignments`, {
    params: {
      flowUuid: payload.flowUuid,
      assignableType: payload.assignableType,
      assignableUuid: payload.assignableUuid,
    },
  })
}

const createModelAssignment = async (
  payload: ICreateFlowAssignmentPayload
): Promise<AxiosResponse<IModel3DAssignment>> => {
  return await client.post<IModel3DAssignment>(
    `/flows/model-assignments`,
    payload
  )
}

const deleteModelAssignment = async (
  assignmentUuid: string
): Promise<AxiosResponse<IModel3DAssignment>> => {
  return await client.delete<IModel3DAssignment>(
    `/flows/model-assignments/${assignmentUuid}`
  )
}

const updateModelAssignment = async (
  assignmentUuid: string,
  payload: IAssignmentUpdatePayload
): Promise<AxiosResponse<IModel3DAssignment>> => {
  return await client.patch<IModel3DAssignment>(
    `/flows/model-assignments/${assignmentUuid}`,
    payload
  )
}

const getFlowRuleProperties = async (
  flowUuid: string
): Promise<AxiosResponse<IFlowRulePropertiesResponse[]>> => {
  return await client.get<IFlowRulePropertiesResponse[]>(
    `/flows/${flowUuid}/rules/summary`
  )
}
const updateModelAssignmentProperties = async (
  assignmentUuid: string,
  payload: IModel3DAssignmentProperty
): Promise<AxiosResponse<IModel3DAssignment>> => {
  return await client.put<IModel3DAssignment>(
    `/flows/model-assignment-properties/${assignmentUuid}`,
    payload
  )
}

const createModel = async (
  payload: ICreateModelPayload
): Promise<AxiosResponse<IModelType>> => {
  return await client.post(`/flows/models`, payload)
}

const createFile = async (
  type: string,
  uuid: string,
  context: ContextValues
): Promise<AxiosResponse<IFile>> => {
  return await client.post<IFile>(`/files`, {
    fileableType: type,
    fileableUuid: uuid,
    context: context,
  })
}

type DownloadFileOpts = {
  onProgressHandler?: (progress: AxiosProgressEvent) => void
  controller?: GenericAbortSignal
}
const downloadFile = async (
  uuid: string,
  opts?: DownloadFileOpts
): Promise<AxiosResponse<Blob>> => {
  if (opts?.controller) {
    return client.get<Blob>(`/files/${uuid}/download`, {
      responseType: 'blob',
      onDownloadProgress: opts.onProgressHandler,
      signal: opts.controller,
    })
  }
  return client.get<Blob>(`/files/${uuid}/download`, {
    responseType: 'blob',
    onDownloadProgress: opts?.onProgressHandler,
  })
}

const getStepOptionModelData = async (
  optionUuid: string
): Promise<AxiosResponse<IModel3DAssignment[]>> => {
  return await client.get<IModel3DAssignment[]>(
    `/flows/options/${optionUuid}/models `
  )
}

const getStepOptionBaseModelData = async (
  optionUuid: string
): Promise<AxiosResponse<IModel3DAssignment[]>> => {
  return await client.get<IModel3DAssignment[]>(
    `/flows/options/${optionUuid}/models `
  )
}

const updateStepOptionModelData = async (
  optionUuid: string,
  payload: UpdateModelPayload[]
): Promise<AxiosResponse<unknown>> => {
  return await client.put<UpdateModelPayload[]>(
    `/flows/options/${optionUuid}/models`,
    {
      data: payload,
    }
  )
}

const publishDraft = async (
  flowUuid: string
): Promise<AxiosResponse<IFlowBase>> => {
  return await client.post<IFlowBase>(`flows/${flowUuid}/publish`)
}

const getSignatures = async (
  flowUuid: string
): Promise<AxiosResponse<IFlowSignature[]>> =>
  await client.get(`flows/${flowUuid}/signatures`)

const updateSignatureIndexes = async (
  flowUuid: string,
  signatureUuids: string[]
): Promise<AxiosResponse<IFlowSignature[]>> =>
  await client.put(`flows/${flowUuid}/signature-indexes`, { signatureUuids })

const getModelGroups = async (
  flowUuid: string
): Promise<AxiosResponse<IModelGroupsResponse[]>> => {
  return await client.get<IModelGroupsResponse[]>(
    `flows/${flowUuid}/model-groups`
  )
}

const createModelGroup = async (
  payload: ICreateModelGroupPayload
): Promise<AxiosResponse<ICreateModelGroupResonse>> => {
  return await client.post<ICreateModelGroupResonse>(
    `flows/model-groups`,
    payload
  )
}

const updateModelGroup = async (
  groupUuid: string,
  payload: IUpdateModelGroup
): Promise<AxiosResponse> => {
  return await client.patch(`flows/model-groups/${groupUuid}`, payload)
}

const syncModelGroupAssignments = async (
  groupUuid: string,
  modelUuids: string[]
): Promise<AxiosResponse> => {
  return await client.put(`flows/model-groups/${groupUuid}/assignments`, {
    modelUuids,
  })
}

const deleteModelGroupAssignment = async (
  groupUuid: string
): Promise<AxiosResponse> => {
  return await client.delete(`flows/model-groups/${groupUuid}`)
}

export const FlowRepository = {
  getFlows,
  createFlowDraft,
  cloneFlowDraft,
  deleteFlowDraft,
  getFlowsMatrix,
  getFlowById,
  updateFlow,
  updateFlowDimensions,
  getNodeById,
  createNode,
  deleteNode,
  updateNodeJson,
  updateNode,
  getStages,
  updateStage,
  deleteStage,
  updateStageIndex,
  updateStepStage,
  updateStepStageIndex,
  createStage,
  getNodeOption,
  getNodeStepsIndexes,
  createModelAssignment,
  deleteModelAssignment,
  getFlowAssignments,
  updateModelAssignment,
  updateModelAssignmentProperties,
  createNodeOption,
  updateOptionProperty,
  removeNodeOption,
  getAllOptionProperties,
  updateNodeOption,
  updateNodeOptionIndexes,
  getIntegrationProperties,
  getFlowRuleProperties,
  createModel,
  createFile,
  getFiles,
  deleteFile,
  getFlowModels,
  getModelGroups,
  createModelGroup,
  updateModelGroup,
  syncModelGroupAssignments,
  deleteModelGroupAssignment,
  downloadFile,
  getStepOptionModelData,
  updateStepOptionModelData,
  publishDraft,
  updateFiles,
  getStepOptionBaseModelData,
  updateStepProperty,
  getSignatures,
  updateSignatureIndexes,
  labels: FlowLabels,
  criteria: FlowCriteria,
  criterionValues: FlowCriterionValues,
}

export const flowPermissions = {
  EDIT: 'flowEngine.update',
} as const
